<script setup lang="ts">
import BaseLayout from "@/layouts/BaseLayout.vue";
import {router} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import {Assessment, CapacityFramework, Form, Participant, TeamRole} from "@/types";
import {useDate} from "@/composables/date";
import ClosedChip from "@/components/chips/ClosedChip.vue";
import CompletedChip from "@/components/chips/CompletedChip.vue";
import OpenChip from "@/components/chips/OpenChip.vue";
import CategoryCard from "@/components/cards/CategoryCard.vue";
import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import AssessmentLayout from "@/layouts/AssessmentLayout.vue";
import ListAnswerComments from "@/Pages/assessments/components/ListAnswerComments.vue";
import DeleteDialog from "@/components/dialogs/DeleteDialog.vue";
import ConfirmDialog from "@/components/dialogs/ConfirmDialog.vue";
import EditDescription from "@/Pages/assessments/components/EditDescription.vue";

defineOptions({layout: [BaseLayout,AssessmentLayout]})

const {lastUsed} = useDate()

interface Props {
    title: string
    capacityFramework: CapacityFramework
    assessment: Assessment
    form: Form
    participants: Array<Participant>
    isMember: boolean
    isMemberOfCapacityFramework: boolean
    role: TeamRole
}

const props = defineProps<Props>();

const isCloseDialogOpen = ref<boolean>(false)
const isCompleteDialogOpen = ref<boolean>(false)

const isReadonly = computed(() => {
    return props.assessment.status === 'completed' || props.assessment.status === 'closed';
})

const updateAnswer = (answerId: number, optionId: string | null) => {
    if (optionId === null) return

    router.put(route('answers.update', {
        assessment: props.assessment.id,
        answer: answerId
    }), {
        option_id: optionId
    }, {
        preserveScroll: true,
    })
}

const updateComment = (answerId: number, optionId: string, comment: string | null) => {
    router.put(route('answers.update', {
        assessment: props.assessment.id,
        answer: answerId,
    }), {
        option_id: optionId,
        comment: comment
    }, {
        preserveScroll: true,
    })
}

const completeAssessment = () => {
    router.put(route('assessments.complete', {
        assessment: props.assessment.id
    }))
}

const closeAssessment = () => {
    router.put(route('assessments.close', {
        assessment: props.assessment.id
    }))
}

const intervalMs = 30000;
let timer: number | null = null;

function startAutoReload(): void {
    if (props.assessment.status !== 'open' || !props.isMember) {
        return;
    }
    stopAutoReload();
    timer = window.setInterval(() => {
        router.reload();
    }, intervalMs);
}

function stopAutoReload(): void {
    if (timer) {
        clearInterval(timer);
        timer = null;
    }
}

onMounted(startAutoReload);
onBeforeUnmount(stopAutoReload);
</script>

<template>
    <open-chip v-if="props.assessment.status === 'open'" />
    <completed-chip v-else-if="props.assessment.status === 'completed'" />
    <closed-chip v-else-if="props.assessment.status === 'closed'" />
    created {{ lastUsed(props.assessment.created_at) }} by <b>{{ props.assessment.user.name }}</b>
    <v-alert
        v-if="props.assessment.is_starred"
        variant="tonal"
        type="info"
        color="amber"
        class="ma-3"
        icon="mdi-star-circle"
        closable
    >
        <template #title>
            <span class="text-body-2 text-black">
                This assessment has been <b>starred</b> by the maintainers of the framework as an example of a good assessment. Feel free to reach out to them for advice!
            </span>
        </template>
        <template #text>
            <v-btn
                class="mt-3 float-end"
                :href="`mailto:${props.assessment.team.email}`"
            >
                Send email
            </v-btn>
        </template>
    </v-alert>
    <v-alert
        v-if="props.assessment.status !== 'completed' && props.role === 'guest'"
        variant="tonal"
        type="info"
        color="primary"
        class="ma-3"
        closable
    >
        <span class="text-body-2 text-black">
            You are viewing this assessment as a <b>guest</b>. You cannot make any changes to it but you can leave internal comments on each answer.
        </span>
    </v-alert>
    <v-alert
        v-if="props.assessment.status === 'completed' && isMember"
        variant="tonal"
        type="info"
        color="primary"
        class="ma-3"
        closable
    >
        <span class="text-body-2 text-black">
            This assessment is <b>{{ props.assessment.status }}</b>. You can no longer make changes to it.
        </span>
    </v-alert>
    <v-alert
        v-if="props.assessment.status === 'closed' && isMember"
        variant="tonal"
        type="info"
        color="error"
        class="ma-3"
        closable
    >
        <span class="text-body-2 text-black">
            This assessment is <b>{{ props.assessment.status }}</b>. You can no longer make changes to it.
        </span>
    </v-alert>
    <v-sheet>
        <p
            v-if="props.assessment.description"
            class="text-pre pa-5"
            v-html="props.assessment.description"
        />
        <category-card
            v-for="(items,category) in props.form"
            :key="category"
            open
        >
            <template #title>
                {{ category }} <span class="float-end text-body-1 text-medium-emphasis">{{ items.filter((item) => item.option_id).length }}/{{ items.length }}</span>
            </template>
            <template #default>
                <v-card
                    v-for="answer in items"
                    :key="answer.id"
                    color="#FAF9FC"
                    border="sm"
                    variant="flat"
                    class="ma-3"
                >
                    <v-card-title
                        class="text-body-2 bg-card-custom border-b-sm"
                    >
                        {{ answer.capacity_indicator.title }}
                        <div class="d-inline-flex float-end">
                            <v-icon-btn
                                v-if="!isReadonly"
                                variant="tonal"
                                :color="answer.option_id ? 'success' : 'primary'"
                                :icon="answer.option_id ? 'mdi-check-circle' : 'mdi-circle-slice-5'"
                                size="22"
                                icon-size="20"
                                readonly
                            />
                        </div>
                    </v-card-title>
                    <v-card-text class="pa-3 bg-white">
                        <p>
                            {{ answer.capacity_indicator.description }}
                        </p>
                        <v-radio-group
                            :model-value="answer.option_id"
                            :readonly="isReadonly || props.role === 'guest'"
                            hide-details
                            @update:model-value="updateAnswer(answer.id, $event)"
                        >
                            <v-radio
                                v-for="option in answer.options"
                                :key="option.id"
                                :value="option.id"
                                color="primary"
                                density="compact"
                                class="mb-2 text-body-2"
                            >
                                <template #label>
                                    <v-list-item class="text-body-2 ">
                                        {{ option.value }}
                                    </v-list-item>
                                </template>
                            </v-radio>
                        </v-radio-group>
                        <edit-description
                            v-if="answer.option_id"
                            :hidden="isReadonly || props.role === 'guest'"
                            :description="answer.comment"
                            @submit="updateComment(answer.id, answer.option_id, $event)"
                        />
                        <p
                            v-if="isReadonly || props.role === 'guest'"
                            class="text-pre-wrap text-medium-emphasis text-body-2"
                        >
                            {{ answer.comment }}
                        </p>
                        <small
                            v-if="answer.user && !isReadonly"
                            class="text-medium-emphasis"
                        >
                            Edited {{ lastUsed(answer.updated_at) }} by {{ answer.user.name }}
                        </small>
                    </v-card-text>
                    <v-card-actions
                        v-if="isMember"
                        class="py-1"
                    >
                        <list-answer-comments
                            :comments="answer.comments"
                            :assessment-id="props.assessment.id"
                            :answer-id="answer.id"
                            :readonly="isReadonly"
                        />
                    </v-card-actions>
                </v-card>
            </template>
        </category-card>
    </v-sheet>
    <v-row>
        <v-col
            cols="12"
        >
            <v-btn
                v-if="!isReadonly"
                color="primary"
                :disabled="props.capacityFramework.capacity_indicators_count !== props.assessment.answers_count"
                @click="isCompleteDialogOpen = true"
            >
                Complete assessment
            </v-btn>
            <v-btn
                v-if="!isReadonly"
                color="error"
                variant="outlined"
                class="float-end"
                @click="isCloseDialogOpen = true"
            >
                <span class="font-weight-regular">
                    Close
                </span>
            </v-btn>
        </v-col>
    </v-row>
    <delete-dialog
        v-model="isCloseDialogOpen"
        @submit="closeAssessment"
    >
        <template #title>
            Close assessment
        </template>
        <template #default>
            Are you sure you want to close this assessment? You will not be able to make any further changes and the assessment will be considered as incomplete.
        </template>
        <template #label>
            Close assessment
        </template>
    </delete-dialog>
    <confirm-dialog
        v-model="isCompleteDialogOpen"
        @submit="completeAssessment"
    >
        <template #title>
            Complete assessment
        </template>
        <template #default>
            Are you sure you want to complete this assessment? You will not be able to make any further changes.
        </template>
        <template #label>
            Complete assessment
        </template>
    </confirm-dialog>
</template>

<style scoped>
:deep(small) {
    font-size: 0.75rem;
}

:deep(.v-timeline-item__body) {
    padding-left: 10px !important;
}

:deep(.v-card-actions) {
    min-height: 36px !important;
}
</style>
